import React, { useMemo, useState, useEffect, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    Box,
    IconButton,
    Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { beautifyNumber } from '../../Utils/FormatNumber';
import JournalGroupService from '../../Services/journalGroup.service';
import DocumentService from '../../Services/documents.service';
import EnumsService from '../../Services/enums.service';
import { toast } from 'react-toastify';

const DuplicateJournalEntriesModal = ({
    open,
    onClose,
    direction,
    filteredAndSortedData,
    handleReopenEdit,
    setEditedItem,
    currentEditedItem, // The item currently being edited (to identify original)
    client,
    token,
}) => {
    const { t } = useTranslation();
    const [duplicateJournalEntries, setDuplicateJournalEntries] = useState([]);
    const [loadingDuplicates, setLoadingDuplicates] = useState(false);
    const [sortCodeIdOptions, setSortCodeIdOptions] = useState([]);
    const [accountCardOptions, setAccountCardOptions] = useState([]);
    // Fetch duplicate journal entries based on reference, amount, and date
    // If item is part of a group, show all items in the group
    const fetchDuplicateJournalEntries = useCallback(async () => {
        if (!currentEditedItem?.id || !token || !client) {
            return;
        }

        setLoadingDuplicates(true);
        try {
            // First, check if item is part of a group
            let currentGroup = null;
            let currentGroupItems = [];
            
            try {
                const response = await JournalGroupService.GetGroupByItemIdOnly(
                    currentEditedItem.id,
                    token
                );
                if (response.ok) {
                    const data = await response.json();
                    if (data.success && data.data) {
                        currentGroup = data.data;
                        currentGroupItems = data.data.items || [];
                    }
                }
            } catch (error) {
                console.error('Error fetching journal group:', error);
            }
            
            // If item is part of a group, use group items
            if (currentGroup && currentGroupItems.length > 0) {
                // Convert group items to the format expected by DuplicateJournalEntriesModal
                const duplicates = currentGroupItems
                    .filter(item => item.item) // Only include items with actual data
                    .map(item => {
                        // Determine docType based on which ID field is present
                        let docType = currentEditedItem.docType; // fallback
                        if (item.incomeId) {
                            docType = 'Incomes';
                        } else if (item.expenseId) {
                            docType = 'Expenses';
                        } else if (item.journalEntryId) {
                            docType = 'JournalEntries';
                        } else if (item.paymentAcceptanceId) {
                            docType = 'PaymentAcceptances';
                        }

                        return {
                            ...item.item,
                            // Ensure we have the necessary fields
                            id: item.incomeId || item.expenseId || item.journalEntryId || item.paymentAcceptanceId,
                            docType: docType,
                            uploadNumber: item.item.uploadNumber,
                            documentDate: item.item.documentDate || item.item.createdAt,
                            createdAt: item.createdAt
                        };
                    })
                    .filter(item => item.id && item.id !== currentEditedItem.id); // Exclude current item

                setDuplicateJournalEntries(duplicates);
                return;
            }

            // Otherwise, search for duplicates by reference and amount across ALL document types
            if (!currentEditedItem?.reference || !currentEditedItem?.amount) {
                toast.warning(t('CannotFindDuplicates') || 'Cannot find duplicates without reference and amount');
                setDuplicateJournalEntries([]);
                return;
            }

            // Get date range (current month ± 1 month)
            const currentDate = currentEditedItem.documentDate ? new Date(currentEditedItem.documentDate) : new Date();
            const startDate = new Date(currentDate);
            startDate.setMonth(startDate.getMonth() - 1);
            startDate.setDate(1);
            const endDate = new Date(currentDate);
            endDate.setMonth(endDate.getMonth() + 1);
            endDate.setDate(0);

            const formattedStartDate = startDate.toISOString();
            const formattedEndDate = endDate.toISOString();

            // Search across all relevant document types to find cross-type duplicates
            const docTypesToSearch = ['Expenses', 'Incomes', 'JournalEntries'];
            const allDuplicates = [];

            const searchPromises = docTypesToSearch.map(async (searchDocType) => {
                try {
                    const response = await DocumentService.GetDocumentbyDateRange(
                        searchDocType,
                        client.caseId,
                        formattedStartDate,
                        formattedEndDate,
                        token
                    );

                    if (response.ok) {
                        const data = await response.json();
                        const result = data.result || data;

                        // Filter duplicates: same reference and similar amount (within 0.01)
                        const duplicates = result.filter(item => {
                            const sameReference = item.reference === currentEditedItem.reference;
                            const sameAmount = Math.abs(parseFloat(item.amount) - parseFloat(currentEditedItem.amount)) < 0.01;
                            const differentId = item.id !== currentEditedItem.id;
                            return sameReference && sameAmount && differentId;
                        });

                        // Tag each duplicate with its docType
                        return duplicates.map(dup => ({
                            ...dup,
                            docType: dup.docType || searchDocType,
                        }));
                    }
                    return [];
                } catch (err) {
                    console.error(`Error searching duplicates in ${searchDocType}:`, err);
                    return [];
                }
            });

            const results = await Promise.all(searchPromises);
            const combinedDuplicates = results.flat();

            // Deduplicate by id (in case same item appears in multiple results)
            const uniqueDuplicates = [];
            const seenIds = new Set();
            for (const dup of combinedDuplicates) {
                if (!seenIds.has(dup.id)) {
                    seenIds.add(dup.id);
                    uniqueDuplicates.push(dup);
                }
            }

            setDuplicateJournalEntries(uniqueDuplicates);
        } catch (error) {
            console.error('Error fetching duplicate journal entries:', error);
            toast.error(t('ErrorFetchingDuplicates') || 'Error fetching duplicates');
            setDuplicateJournalEntries([]);
        } finally {
            setLoadingDuplicates(false);
        }
    }, [currentEditedItem, client, token, t]);

    // Fetch sort codes and account cards when modal opens
    const fetchEnums = useCallback(async () => {
        if (!client || !token) return;

        try {
            // Fetch sort codes
            const codeIdResponse = await EnumsService.GetSortCodesByAccountantId(client.accountantId, token);
            if (codeIdResponse.ok) {
                const codeIdData = await codeIdResponse.json();
                setSortCodeIdOptions(codeIdData);
            }

            // Fetch account cards
            const accountsResponse = await EnumsService.GetAccountCardsByCaseId(client.caseId, token);
            if (accountsResponse.ok) {
                const accountsData = await accountsResponse.json();
                const accountCardOptionsEdit = [{ name: t('AddAccountCard'), value1: 'AddItem' }, ...accountsData];
                setAccountCardOptions(accountCardOptionsEdit);
            }
        } catch (error) {
            console.error('Error fetching enums:', error);
        }
    }, [client, token, t]);

    // Fetch duplicate journal entries when modal opens
    useEffect(() => {
        if (open && currentEditedItem?.id) {
            fetchEnums();
            fetchDuplicateJournalEntries();
        } else if (!open) {
            // Clear data when modal closes
            setDuplicateJournalEntries([]);
        }
    }, [open, currentEditedItem?.id, fetchDuplicateJournalEntries, fetchEnums]);

    // Determine which entry is the original
    // Original is identified by:
    // 1. Entry that doesn't have "שוכפל מ" (duplicated from) in description, OR
    // 2. Entry whose uploadNumber matches the number extracted from other entries' "שוכפל מ X" descriptions
    const originalEntryId = useMemo(() => {
        if (!currentEditedItem || duplicateJournalEntries.length === 0) return null;
        
        // Combine current item with duplicates for comparison
        const allEntries = [
            { ...currentEditedItem, isCurrent: true },
            ...duplicateJournalEntries.map(e => ({ ...e, isCurrent: false }))
        ];
        
        // Extract source upload number from descriptions that contain "שוכפל מ" (duplicated from)
        let sourceUploadNumber = null;
        for (const entry of allEntries) {
            const description = entry.description || '';
            // Check for "שוכפל מ" pattern (duplicated from)
            const match = description.match(/שוכפל מ\s*(\d+)/);
            if (match && match[1]) {
                sourceUploadNumber = parseInt(match[1], 10);
                break; // Found the source number
            }
        }
        
        // Find the original entry
        if (sourceUploadNumber !== null) {
            // Original is the entry with matching uploadNumber
            const original = allEntries.find(e => e.uploadNumber === sourceUploadNumber);
            if (original) {
                return original.id;
            }
        }
        
        // Fallback: Find entry without "שוכפל מ" in description
        const entryWithoutDuplicateText = allEntries.find(e => {
            const description = e.description || '';
            return !description.includes('שוכפל מ');
        });
        if (entryWithoutDuplicateText) {
            return entryWithoutDuplicateText.id;
        }
        
        // Last fallback: Find the entry with the earliest documentDate
        const sortedByDate = allEntries
            .filter(e => e.documentDate)
            .sort((a, b) => new Date(a.documentDate) - new Date(b.documentDate));
        
        if (sortedByDate.length > 0) {
            return sortedByDate[0].id;
        }
        
        // If no dates, use the earliest createdDate if available
        const sortedByCreated = allEntries
            .filter(e => e.createdDate)
            .sort((a, b) => new Date(a.createdDate) - new Date(b.createdDate));
        
        return sortedByCreated.length > 0 ? sortedByCreated[0].id : null;
    }, [currentEditedItem, duplicateJournalEntries]);

    // Helper function to get sort code name
    const getSortCodeName = (sortCodeId) => {
        if (!sortCodeId) return '-';
        const sortCode = sortCodeIdOptions.find(option => option.sortCodeId === sortCodeId);
        return sortCode ? `${sortCode.sortCodeId} - ${sortCode.sortCodeName}` : sortCodeId;
    };

    // Helper function to get account card name
    const getAccountCardName = (accountId) => {
        if (!accountId) return '-';
        const accountCard = accountCardOptions.find(option => option.cardId === accountId);
        return accountCard ? accountCard.name : accountId;
    };

    // Helper function to get translated document type label
    const getDocTypeLabel = (docType) => {
        switch (docType) {
            case 'Expenses': return t('Expenses');
            case 'Incomes': return t('Incomes');
            case 'JournalEntries': return t('JournalEntries');
            case 'PaymentAcceptances': return t('PaymentAcceptances');
            default: return docType || '-';
        }
    };

    // Helper to get a color chip for each doc type
    const getDocTypeColor = (docType) => {
        switch (docType) {
            case 'Expenses': return '#F44336';
            case 'Incomes': return '#4CAF50';
            case 'JournalEntries': return '#2196F3';
            case 'PaymentAcceptances': return '#FF9800';
            default: return '#757575';
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '8px',
                    direction: direction,
                    maxWidth: '65vw',
                }
            }}
        >
            <DialogTitle dir={direction} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">{t('DuplicateJournal')}</Typography>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                {loadingDuplicates ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}>
                        <Typography>{t('Loading') || 'Loading...'}</Typography>
                    </Box>
                ) : duplicateJournalEntries.length === 0 ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}>
                        <Typography>{t('NoDuplicatesFound') || 'No duplicate journal entries found'}</Typography>
                    </Box>
                ) : (
                    <TableContainer dir={direction} component={Paper} sx={{ maxHeight: 400 }}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ 
                                        fontWeight: 700, 
                                        fontSize: 12,
                                        backgroundColor: '#304FFF',
                                        color: '#ffffff',
                                        textAlign: 'left',
                                        width: '80px'
                                    }}>
                                        {t('Entry') || 'Entry'}
                                    </TableCell>
                                    <TableCell sx={{ 
                                        fontWeight: 700, 
                                        fontSize: 12,
                                        backgroundColor: '#304FFF',
                                        color: '#ffffff',
                                        textAlign: 'left'
                                    }}>
                                        {t('Type') || 'Type'}
                                    </TableCell>
                                    <TableCell sx={{ 
                                        fontWeight: 700, 
                                        fontSize: 12,
                                        backgroundColor: '#304FFF',
                                        color: '#ffffff',
                                        textAlign: 'left'
                                    }}>
                                        {t('UploadNumber') || 'Upload #'}
                                    </TableCell>
                                    <TableCell sx={{ 
                                        fontWeight: 700, 
                                        fontSize: 12,
                                        backgroundColor: '#304FFF',
                                        color: '#ffffff',
                                        textAlign: 'left'
                                    }}>
                                        {t('Code ID') || 'Sort Code'}
                                    </TableCell>
                                    <TableCell sx={{ 
                                        fontWeight: 700, 
                                        fontSize: 12,
                                        backgroundColor: '#304FFF',
                                        color: '#ffffff',
                                        textAlign: 'left'
                                    }}>
                                        {t('Debit Account') || 'Debit Account'}
                                    </TableCell>
                                    <TableCell sx={{ 
                                        fontWeight: 700, 
                                        fontSize: 12,
                                        backgroundColor: '#304FFF',
                                        color: '#ffffff',
                                        textAlign: 'left'
                                    }}>
                                        {t('Credit Account') || 'Credit Account'}
                                    </TableCell>
                                    <TableCell sx={{ 
                                        fontWeight: 700, 
                                        fontSize: 12,
                                        backgroundColor: '#304FFF',
                                        color: '#ffffff',
                                    }}>
                                        {t('Description') || 'Description'}
                                    </TableCell>
                                    <TableCell sx={{ 
                                        fontWeight: 700, 
                                        fontSize: 12,
                                        backgroundColor: '#304FFF',
                                        color: '#ffffff',
                                        textAlign: 'left'
                                    }}>
                                        {t('Reference') || 'Reference'}
                                    </TableCell>
                                    <TableCell sx={{ 
                                        fontWeight: 700, 
                                        fontSize: 12,
                                        backgroundColor: '#304FFF',
                                        color: '#ffffff',
                                        textAlign: 'left'
                                    }}>
                                        {t('Amount') || 'Amount'}
                                    </TableCell>
                                    <TableCell sx={{ 
                                        fontWeight: 700, 
                                        fontSize: 12,
                                        backgroundColor: '#304FFF',
                                        color: '#ffffff',
                                        textAlign: 'left'
                                    }}>
                                        {t('Document Date') || 'Date'}
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {duplicateJournalEntries.map((entry) => {
                                    const isOriginal = entry.id === originalEntryId;
                                    return (
                                        <TableRow
                                            key={entry.id}
                                            hover
                                            onClick={async () => {
                                                onClose();
                                                
                                                // Use entry.docType which was already correctly determined during mapping
                                                // It should be one of: 'Expenses', 'Incomes', 'JournalEntries', 'PaymentAcceptances'
                                                let docType = entry.docType;
                                                
                                                // Fallback if docType is missing
                                                if (!docType) {
                                                    if (currentEditedItem?.docType) {
                                                        docType = currentEditedItem.docType;
                                                    } else {
                                                        docType = 'JournalEntries'; // Default fallback
                                                    }
                                                }
                                                
                                                try {
                                                    // Fetch full document data to ensure we have all fields including documentUrl
                                                    // API expects plural form: 'Expenses', 'Incomes', 'JournalEntries', 'PaymentAcceptances'
                                                    const response = await DocumentService.GetDocumentById(
                                                        docType,
                                                        client.caseId,
                                                        entry.id,
                                                        token
                                                    );
                                                    
                                                    if (response.ok) {
                                                        const fullDocumentData = await response.json();
                                                        // Ensure docType and documentType are set correctly
                                                        fullDocumentData.docType = docType;
                                                        fullDocumentData.documentType = docType;
                                                        
                                                        // Find the index in filteredAndSortedData
                                                        const index = filteredAndSortedData.findIndex(item => item.id === entry.id);
                                                        if (index !== -1 && handleReopenEdit) {
                                                            await handleReopenEdit(fullDocumentData, index);
                                                        } else {
                                                            // If not found in current data, set edited item directly
                                                            setEditedItem(fullDocumentData);
                                                            if (handleReopenEdit) {
                                                                await handleReopenEdit(fullDocumentData, 0);
                                                            }
                                                        }
                                                    } else {
                                                        // If fetch fails, use the entry data we have but ensure docType is set
                                                        const entryWithDocType = {
                                                            ...entry,
                                                            docType: docType,
                                                            documentType: docType
                                                        };
                                                        const index = filteredAndSortedData.findIndex(item => item.id === entry.id);
                                                        if (index !== -1 && handleReopenEdit) {
                                                            await handleReopenEdit(entryWithDocType, index);
                                                        } else {
                                                            setEditedItem(entryWithDocType);
                                                            if (handleReopenEdit) {
                                                                await handleReopenEdit(entryWithDocType, 0);
                                                            }
                                                        }
                                                    }
                                                } catch (error) {
                                                    console.error('Error fetching full document data:', error);
                                                    // Fallback: use the entry data we have but ensure docType is set
                                                    const entryWithDocType = {
                                                        ...entry,
                                                        docType: docType,
                                                        documentType: docType
                                                    };
                                                    const index = filteredAndSortedData.findIndex(item => item.id === entry.id);
                                                    if (index !== -1 && handleReopenEdit) {
                                                        await handleReopenEdit(entryWithDocType, index);
                                                    } else {
                                                        setEditedItem(entryWithDocType);
                                                        if (handleReopenEdit) {
                                                            await handleReopenEdit(entryWithDocType, 0);
                                                        }
                                                    }
                                                }
                                            }}
                                            sx={{
                                                cursor: 'pointer',
                                                backgroundColor: isOriginal ? '#E8F5E9' : 'transparent',
                                                '&:hover': {
                                                    backgroundColor: isOriginal ? '#C8E6C9' : '#f5f5f5'
                                                },
                                                '& td': {
                                                    fontSize: 12
                                                }
                                            }}
                                        >
                                            <TableCell>
                                                {isOriginal && (
                                                    <Chip
                                                        label={t('Original') || 'Original'}
                                                        size="small"
                                                        sx={{
                                                            backgroundColor: '#4CAF50',
                                                            color: '#ffffff',
                                                            fontSize: '10px',
                                                            height: '20px',
                                                            fontWeight: 600
                                                        }}
                                                    />
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={getDocTypeLabel(entry.docType)}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: getDocTypeColor(entry.docType),
                                                        color: '#ffffff',
                                                        fontSize: '10px',
                                                        height: '20px',
                                                        fontWeight: 600
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell>{entry.uploadNumber || '-'}</TableCell>
                                        <TableCell>{getSortCodeName(entry.sortCodeId)}</TableCell>
                                        <TableCell>{getAccountCardName(entry.debitAccount)}</TableCell>
                                        <TableCell>{getAccountCardName(entry.creditAccount)}</TableCell>
                                        <TableCell>{entry.description || '-'}</TableCell>
                                        <TableCell>{entry.reference || '-'}</TableCell>
                                        <TableCell>{beautifyNumber(entry.amount) || '-'}</TableCell>
                                        <TableCell>
                                            {entry.documentDate ? new Date(entry.documentDate).toLocaleDateString() : '-'}
                                        </TableCell>
                                    </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default DuplicateJournalEntriesModal;

